import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Tab,
    Tabs,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Chip,
    Card
} from '@mui/material';
import {
    LocalPharmacy as LocalPharmacyIcon,
    LocalHospital as LocalHospitalIcon,
    LocationOn as LocationOnIcon,
    Store as StoreIcon,
    Edit as EditIcon,
    AccessTimeFilled as AccessTimeFilledIcon,
    Close as CloseIcon,
    PersonOff as PersonOffIcon
} from '@mui/icons-material';
import Cards from '../../ReusableComponents/Cards/Cards';
import AddStore from './AddStore';
import SearchBar from '../../ReusableComponents/Search/SearchBar';
import CustomSnackbar from '../../components/CustomSnackbar';
import api from '../../api/api';
import { ButtonStyles } from '../../Styles/ButtonStyles';
import en_ja from '../../translations/en_ja';
import { Pagination } from '@mui/material';

const DEFAULT_COORDS = { lat: 35.6762, lng: 139.6503 };

export default function Stores() {
    const [searchTerm, setSearchTerm] = useState('');
    const [storeData, setStoreData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedStore, setSelectedStore] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarType, setSnackbarType] = useState('success');
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [storeToDeactivate, setStoreToDeactivate] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapError, setMapError] = useState("");
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef([]);
    const [cardsData, setCardsData] = useState([]);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(100); // fetch 100 stores at a time
    const [totalPages, setTotalPages] = useState(1);

    const getAllCards = () => {
        api
            .get("/v1/admin/stores/dashboard-data")
            .then((res) => {
                setCardsData(res?.data ?? {});
            })
            .catch((err) => console.error(err));
    };

    useEffect(() => {
        getAllStores();
        getAllCards();
    }, []);

    useEffect(() => {
        if (storeData.length > 0) {
            loadGoogleMaps();
        }
    }, [storeData]);

    const loadGoogleMaps = () => {
        return new Promise((resolve, reject) => {
            if (window.google && window.google.maps && window.google.maps.Map) {
                setMapLoaded(true);
                initializeMap();
                resolve();
                return;
            }
            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
            if (existingScript) {
                const checkGoogleMapsReady = () => {
                    if (window.google && window.google.maps && window.google.maps.Map) {
                        setMapLoaded(true);
                        initializeMap();
                        resolve();
                    } else { setTimeout(checkGoogleMapsReady, 100); }
                };
                checkGoogleMapsReady();
                return;
            }
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD-206GuB2zh4Pl6c2EEf4c1U0Qw-ju9kY&libraries=places&callback=initGoogleMaps`;
            script.async = true;
            script.defer = true;
            window.initGoogleMaps = () => {
                if (window.google && window.google.maps && window.google.maps.Map) {
                    setMapLoaded(true);
                    initializeMap();
                    resolve();
                } else {
                    setMapError(en_ja["Google Maps failed to initialize properly"]);
                    reject(new Error("Google Maps failed to initialize"));
                }
            };
            script.onerror = () => {
                setMapError(en_ja["Failed to load Google Maps script"]);
                reject(new Error("Failed to load Google Maps script"));
            };
            document.head.appendChild(script);
        });
    };

    const initializeMap = () => {
        if (!window.google || !window.google.maps || !window.google.maps.Map || !mapRef.current) {
            // console.log("Google Maps not ready or map container not available")
            return
        }
        try {
            const map = new window.google.maps.Map(mapRef.current, {
                center: DEFAULT_COORDS,
                zoom: 11,
            });
            mapInstance.current = map;
            addStoreMarkers();
        } catch (error) {
            console.error(error);
            setMapError(en_ja["Failed to initialize map"]);
        }
    };

    const addStoreMarkers = () => {
        if (!mapInstance.current || !window.google) return;
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        const bounds = new window.google.maps.LatLngBounds();
        const geocoder = new window.google.maps.Geocoder();
        filteredStores.forEach((store, index) => {
            const lat = parseFloat(store.latitude);
            const lng = parseFloat(store.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                createMarker(store, index, lat, lng, bounds);
            } else if (store.address) {
                geocoder.geocode({ address: store.address }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        const location = results[0].geometry.location;
                        createMarker(store, index, location.lat(), location.lng(), bounds);
                        // console.log(`Geocoded ${store.store_name}: ${location.lat()}, ${location.lng()}`);
                    } else {
                        console.warn(`${en_ja['Could not geocode address for']} ${store.store_name}: ${store.address}`);
                    }
                });
            }
        });
        setTimeout(() => {
            if (markersRef.current.length > 0 && !bounds.isEmpty()) {
                mapInstance.current.fitBounds(bounds);
                if (markersRef.current.length === 1) {
                    mapInstance.current.setZoom(15);
                }
            }
        }, 1000);
    };

    const createMarker = (store, index, lat, lng, bounds) => {
        const position = new window.google.maps.LatLng(lat, lng);
        const marker = new window.google.maps.Marker({
            position,
            map: mapInstance.current,
            title: store.store_name,
            icon: {
                url: store.status ? 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
<circle cx="16" cy="16" r="12" fill="#22c55e" stroke="white" stroke-width="3"/>
<text x="16" y="20" text-anchor="middle" fill="white" font-size="14" font-weight="bold">${index + 1}</text>
</svg>`) : 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
<circle cx="16" cy="16" r="12" fill="#ef4444" stroke="white" stroke-width="3"/>
<text x="16" y="20" text-anchor="middle" fill="white" font-size="14" font-weight="bold">${index + 1}</text>
</svg>`),
                scaledSize: new window.google.maps.Size(32, 32)
            }
        });
        const infoWindow = new window.google.maps.InfoWindow({
            content: `
<div style="padding: 8px;">
<h4 style="margin: 0 0 8px 0; color: #333;">${store.store_name}</h4>
<p style="margin: 0 0 4px 0; color: #666; font-size: 12px;">${store.address}</p>
<p style="margin: 0; color: #666; font-size: 12px;">⏰ ${formatTime12(store.from)} - ${formatTime12(store.to)}</p>
</div>`
        });
        marker.addListener('click', () => {
            infoWindow.open(mapInstance.current, marker);
        });
        markersRef.current.push(marker);
        bounds.extend(position);
    };

    const getAllStores = async (currentPage = 1) => {
        setLoading(true);
        setError(false);
        try {
            const res = await api.get(`/v1/admin/all-stores?search=''&page=${currentPage}&limit=${limit}`);
            setStoreData(res.data?.data ?? []);
            const totalItems = res.data?.total ?? 0;
            setTotalPages(Math.ceil(totalItems / limit));
        } catch (err) {
            console.error(err);
            setError(true);
            setSnackbarMessage(en_ja['Failed to fetch stores']);
            setSnackbarType('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllStores(page);
    }, [page]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const filteredStores = storeData
        // .filter((store) => tab === 0 ? true : tab === 1 ? store.status === true : store.status === false)
        .filter((store) =>
            store.store_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            store.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            store.prefectures?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            store.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );

    useEffect(() => {
        if (storeData.length > 0) {
            loadGoogleMaps();
        }
    }, [storeData]);

    useEffect(() => {
        if (mapLoaded && mapInstance.current) {
            addStoreMarkers();
        }
    }, [filteredStores, mapLoaded]);

    const totalStores = storeData.length;
    // const activeStores = storeData.filter(store => store.status === true).length;
    // const inactiveStores = storeData.filter(store => store.status === false).length;

    // const handleTabChange = (event, newValue) => setTab(newValue);
    const handleAddStore = () => {
        setEditMode(false);
        setSelectedStore(null);
        setDialogOpen(true);
    };

    const handleEditStore = (store) => {
        setEditMode(true);
        setSelectedStore(store);
        setDialogOpen(true);
    };

    const handleDeactivateClick = (store) => {
        setStoreToDeactivate(store);
        setConfirmDialogOpen(true);
    };
    const handleConfirmDeactivate = async () => {
        if (!storeToDeactivate) return;
        const obj = { status: !storeToDeactivate.status };
        try {
            const res = await api.put(`/v1/admin/store/status/${storeToDeactivate.id}`, obj);
            const updatedStatus = res?.data?.store?.status ? en_ja['Active'] : en_ja['Inactive'];
            setSnackbarMessage(`${en_ja['Store status updated to']} ${updatedStatus}`);
            setSnackbarType('success');
            setSnackbarOpen(true);
            getAllStores();
        } catch (err) {
            console.error(err);
            setSnackbarMessage(en_ja['Could not update status']);
            setSnackbarType('error');
            setSnackbarOpen(true);
        } finally {
            setConfirmDialogOpen(false);
            setStoreToDeactivate(null);
        }
    };

    const handleCancelDeactivate = () => {
        setConfirmDialogOpen(false);
        setStoreToDeactivate(null);
    };
    const handleSnackbarClose = () => setSnackbarOpen(false);
    const formatTime12 = (time) => {
        if (!time) return '';
        const [hourStr, min] = time.split(':');
        let hour = parseInt(hourStr, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12 || 12;
        return `${hour}:${min} ${ampm}`;
    };
    return (
        <Box sx={{ mt: 2 }}>
            <CustomSnackbar isOpen={snackbarOpen} type={snackbarType} message={snackbarMessage} onClose={handleSnackbarClose} />

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 3 }}>
                <Cards title={en_ja['Total Stores']} value={cardsData?.total ?? 0} icon={<LocalPharmacyIcon sx={{ color: 'white', fontSize: 24 }} />} />
                <Cards title={en_ja['Active Stores']} value={cardsData?.totalActive ?? 0} icon={<LocalHospitalIcon sx={{ color: 'white', fontSize: 24 }} />} />
                <Cards title={en_ja['Inactive Stores']} value={cardsData?.totalInactive ?? 0} icon={<LocationOnIcon sx={{ color: 'white', fontSize: 24 }} />} />
                <Cards title={en_ja['Avg Revenue/Store']} value="¥4.2M" icon={<StoreIcon sx={{ color: 'white', fontSize: 24 }} />} />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    my: 3
                }}
            >

                <Box sx={{ display: "flex", gap: 2 }}>

                    <SearchBar value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={en_ja['Search by store name or city']} />

                </Box>

                <Button sx={ButtonStyles} onClick={handleAddStore}>
                    {en_ja['Add New Store']}
                </Button>

            </Box>

            <Typography
                variant="h5"
                fontWeight="700"
                color="#1a1a1a"
                sx={{ fontSize: "20px", mb: 3 }}
            >
                {en_ja['Store Locations']}
            </Typography>

            <Card sx={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)', border: '1px solid rgba(0, 0, 0, 0.05)', overflow: 'hidden', height: "70vh" }}>
                <Box sx={{ display: "flex", height: "100%" }}>
                    <Box sx={{ width: "40%", borderRight: 1, borderColor: "divider" }}>
                        <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
                            <Typography variant="h6" fontWeight={600}>{en_ja['Store List']}</Typography>
                            <Typography variant="caption" color="text.secondary">{en_ja['Click on any store to see location on map']}</Typography>
                        </Box>
                        <Box sx={{ height: "calc(100% - 150px)", overflowY: "auto", p: 2 }}>
                            {loading ? (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <CircularProgress size={28} />
                                    <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>{en_ja['Loading stores...']}</Typography>
                                </Box>
                            ) : error ? (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="h6" color="error">{en_ja['Failed to fetch stores']}</Typography>
                                </Box>
                            ) : filteredStores.length === 0 ? (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="h6" color="error">{en_ja['No stores available']}</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {filteredStores.map((store, index) => (
                                        <Paper key={store.id} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e9ecef', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'all 0.2s ease', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1, fontSize: '16px' }}>{store.store_name}</Typography>
                                                    <Chip label={store.status ? en_ja['ACTIVE'] : en_ja['INACTIVE']} size="small" sx={{ backgroundColor: store.status ? '#22c55e' : '#ef4444', color: 'white', fontWeight: 600, fontSize: '10px', mb: 1 }} />
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton size="small" onClick={() => handleEditStore(store)} sx={{ color: '#6b7280' }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => handleDeactivateClick(store)} sx={{ color: '#ef4444' }}>
                                                        <PersonOffIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <LocationOnIcon fontSize="small" sx={{ color: '#6b7280', mt: 0.2, flexShrink: 0 }} />
                                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: '12px' }}>{store.address}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccessTimeFilledIcon fontSize="small" sx={{ color: '#6b7280' }} />
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>{formatTime12(store.from)} - {formatTime12(store.to)}</Typography>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {totalPages > 1 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    borderTop: 1,
                                    p: 1,
                                    borderColor: "divider",
                                    background: "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)",
                                }}
                            >
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    shape="rounded"
                                    showFirstButton
                                    showLastButton
                                    sx={{
                                        "& .MuiPaginationItem-root": {
                                            fontSize: "14px",
                                            fontWeight: 500,
                                            color: "#495057",
                                            borderRadius: "8px",
                                            transition: "all 0.2s ease",
                                        },
                                        "& .MuiPaginationItem-root:hover": {
                                            backgroundColor: "rgba(220,53,69,0.04)",
                                            color: "#dc3545",
                                            transform: "scale(1.05)",
                                        },
                                        "& .Mui-selected": {
                                            backgroundColor: "#dc3545",
                                            color: "#fff",
                                            "&:hover": {
                                                backgroundColor: "#dc3545",
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        )}

                    </Box>
                    <Box sx={{ flex: 1, position: "relative", display: "flex", flexDirection: "column" }}>
                        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", backgroundColor: "#f8f9fa", flexShrink: 0 }}>
                            <Typography variant="h6" fontWeight={600} sx={{ fontSize: "16px" }}>
                                {en_ja["Store Location Map"]}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {en_ja["Map showing all store locations with pins. Click on pins to highlight store details"]}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, position: "relative", minHeight: "400px" }}>
                            {mapError ? (
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column" }}>
                                    <Typography color="error" gutterBottom>{mapError}</Typography>
                                    <Button onClick={() => { setMapError(""); loadGoogleMaps(); }}>
                                        {en_ja["Retry"]}
                                    </Button>
                                </Box>
                            ) : !mapLoaded ? (
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                    <CircularProgress size={28} />
                                    <Typography sx={{ ml: 2 }}>{en_ja["Loading map..."]}</Typography>
                                </Box>
                            ) : (
                                <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Card>

            <AddStore
                dialogOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                editMode={editMode}
                storeData={selectedStore}
                onSubmit={getAllStores}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarType={setSnackbarType}
                setSnackbarMessage={setSnackbarMessage}
            />

            <Dialog open={confirmDialogOpen} onClose={handleCancelDeactivate} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" textAlign="center" sx={{ flexGrow: 1 }}>
                        {storeToDeactivate?.status ? en_ja["Deactivate Store"] : en_ja["Activate Store"]}
                    </Typography>
                    <IconButton onClick={handleCancelDeactivate}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pt: 1, pb: 3 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        {en_ja["Are you sure you want to"]} {storeToDeactivate?.status ? en_ja["deactivate"] : en_ja["activate"]}
                        {" "} "{storeToDeactivate?.store_name}"?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {en_ja["This action can be reversed later by"]} {storeToDeactivate?.status ? en_ja["reactivating"] : en_ja["deactivating"]} {en_ja["the account."]}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button variant="outlined" onClick={handleCancelDeactivate} sx={{ textTransform: 'none' }}>
                        {en_ja["No, Keep"]} {storeToDeactivate?.status ? en_ja["Active"] : en_ja["Inactive"]}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDeactivate}
                        color={storeToDeactivate?.status ? "error" : "success"}
                        sx={{ textTransform: 'none' }}
                    >
                        {en_ja["Yes,"]} {storeToDeactivate?.status ? en_ja["Deactivate"] : en_ja["Activate"]}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
